import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { vehiclesData } from '../data/vehiclesData';

// Image Carousel Component with swipe support
const ImageCarousel = ({ images, vehicleName, vehicleYear, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const distance = touchStartX.current - touchEndX.current;
    
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swipe left - next image
        setCurrentIndex((prev) => (prev + 1) % images.length);
      } else {
        // Swipe right - previous image
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const goToPrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToIndex = (index, e) => {
    e.stopPropagation();
    setCurrentIndex(index);
  };

  return (
    <div 
      className="relative h-64 sm:h-80 md:h-96 overflow-hidden bg-zinc-800"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Main Image */}
      <img
        src={images[currentIndex]}
        alt={`${vehicleName} ${vehicleYear}`}
        className="w-full h-full object-cover object-center cursor-pointer"
        onClick={onImageClick}
        draggable={false}
      />

      {/* Navigation Arrows (visible on hover/desktop) */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all duration-300 opacity-0 hover:opacity-100 md:opacity-70"
            aria-label="Imagen anterior"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all duration-300 opacity-0 hover:opacity-100 md:opacity-70"
            aria-label="Imagen siguiente"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => goToIndex(index, e)}
              className={`rounded-full transition-all duration-300 shadow-md ${
                currentIndex === index
                  ? 'bg-white w-3 h-3'
                  : 'bg-white/60 w-2.5 h-2.5 hover:bg-white/80'
              }`}
              aria-label={`Ir a imagen ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const VehiclesList = () => {
  const [sortBy, setSortBy] = useState('favorites');
  const navigate = useNavigate();
  const whatsappNumber = "18099640099";

  // Get vehicle tags
  const getVehicleTags = (vehicle) => {
    const tags = [];
    
    if (vehicle.year >= 2024) {
      tags.push({ text: 'Nuevo', color: 'bg-green-600' });
    }
    
    if (vehicle.price >= 250) {
      tags.push({ text: 'Lujo', color: 'bg-yellow-600' });
    }
    
    if (vehicle.featured) {
      tags.push({ text: 'Más Reservado', color: 'bg-yellow-700' });
    }
    
    if (vehicle.price >= 60 && vehicle.price <= 85) {
      tags.push({ text: 'Mejor Calidad-Precio', color: 'bg-blue-600' });
    }
    
    if (vehicle.fuel.toLowerCase().includes('híbrido') || vehicle.fuel.toLowerCase().includes('hybrid')) {
      tags.push({ text: 'Híbrido', color: 'bg-green-700' });
    }
    
    if (vehicle.passengers >= 7) {
      tags.push({ text: '7 Pasajeros', color: 'bg-purple-600' });
    }
    
    return tags;
  };

  // Priority vehicles - these always appear first when "favorites" is selected
  const priorityVehicleIds = [
    'honda-crv-2024-negra',      // CR-V 2024 interior rojo
    'honda-crv-2020-plata',      // CR-V 2020 gris $60
    'honda-crv-2021-negra',      // CR-V 2021 negra $65
    'toyota-rav4-2025',          // RAV4 2025 blanca $80
    'honda-crv-2025-hybrid-azul' // CR-V Hybrid 2025 azul $95
  ];

  // Sort vehicles based on selected option
  const sortedVehicles = useMemo(() => {
    // If "favorites" is selected, show priority vehicles first
    if (sortBy === 'favorites') {
      const priorityVehicles = priorityVehicleIds
        .map(id => vehiclesData.find(v => v.id === id))
        .filter(Boolean);
      
      const otherVehicles = vehiclesData
        .filter(v => !priorityVehicleIds.includes(v.id))
        .sort((a, b) => a.price - b.price); // Others sorted by price low to high
      
      return [...priorityVehicles, ...otherVehicles];
    }
    
    // For other sort options, sort all vehicles normally
    const sorted = [...vehiclesData];
    switch (sortBy) {
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'year-new':
        return sorted.sort((a, b) => b.year - a.year);
      case 'year-old':
        return sorted.sort((a, b) => a.year - b.year);
      default:
        return sorted.sort((a, b) => a.price - b.price);
    }
  }, [sortBy]);

  const handleWhatsApp = (vehicle) => {
    // Create a more descriptive message with vehicle ID for identification
    const vehicleRef = vehicle.id.replace(/-/g, ' ').toUpperCase();
    const message = `Hola, vengo de la página web de La Rent Car de la Isla y estoy interesado en este vehículo:\n\n🚗 *${vehicle.name} ${vehicle.year}*\n📋 Código: ${vehicleRef}\n💰 Precio: $${vehicle.price} USD/día\n\n¿Está disponible?`;
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleViewDetails = (vehicleId) => {
    navigate(`/vehiculo/${vehicleId}`);
  };

  return (
    <section id="vehiculos" className="py-16 md:py-24 bg-black">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Nuestros Vehículos
          </h2>
          <p className="text-white/70 text-base md:text-lg">
            Vehículos disponibles desde ${Math.min(...sortedVehicles.map(v => v.price))}/día
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Sort options */}
            <div className="flex-1">
              <label className="block text-white/60 text-sm mb-2">Ordenar por</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-zinc-900 border border-white/20 text-white px-4 py-3 appearance-none cursor-pointer hover:border-yellow-600/50 transition-colors duration-300"
              >
                <option value="favorites">⭐ Más Solicitados</option>
                <option value="price-low">Precio: Menor a Mayor</option>
                <option value="price-high">Precio: Mayor a Menor</option>
                <option value="year-new">Año: Más Nuevo</option>
                <option value="year-old">Año: Más Antiguo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Vehicles List */}
        <div className="space-y-6">
          {sortedVehicles.map((vehicle) => {
            const tags = getVehicleTags(vehicle);
            
            return (
              <div
                key={vehicle.id}
                className="bg-zinc-900 border border-white/10 hover:border-yellow-600/50 transition-all duration-300 overflow-hidden"
              >
                {/* Vehicle Image Carousel */}
                <div className="relative">
                  <ImageCarousel 
                    images={vehicle.images}
                    vehicleName={vehicle.name}
                    vehicleYear={vehicle.year}
                    onImageClick={() => handleViewDetails(vehicle.id)}
                  />
                  
                  {/* Tags Overlay */}
                  {tags.length > 0 && (
                    <div className="absolute top-4 left-4 flex flex-wrap gap-2 pointer-events-none">
                      {tags.map((tag) => (
                        <span
                          key={tag.text}
                          className={`${tag.color} text-white px-3 py-1 text-xs font-semibold`}
                        >
                          {tag.text}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Vehicle Info */}
                <div className="p-5 md:p-6">
                  {/* Name and Year */}
                  <div className="mb-4">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                      {vehicle.name}
                    </h3>
                    <p className="text-white/60 text-sm">Año {vehicle.year}</p>
                  </div>

                  {/* Description */}
                  <p className="text-white/70 text-sm md:text-base mb-4 line-clamp-2">
                    {vehicle.description}
                  </p>

                  {/* Price and Specs Row */}
                  <div className="flex items-center justify-between mb-5 pb-5 border-b border-white/10">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl md:text-4xl font-bold text-yellow-600">
                          ${vehicle.price}
                        </span>
                        <span className="text-white/70 text-base">USD/día</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-white/70">
                      <Users className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm md:text-base">{vehicle.passengers} Personas</span>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => handleWhatsApp(vehicle)}
                      className="w-full bg-yellow-700 text-white py-4 px-6 font-semibold text-base md:text-lg hover:bg-yellow-800 transition-all duration-300 flex items-center justify-center gap-3"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Reservar por WhatsApp
                    </button>
                    <button
                      onClick={() => handleViewDetails(vehicle.id)}
                      className="w-full bg-white/10 text-white py-3 px-6 font-semibold border border-white/20 hover:bg-white hover:text-black transition-all duration-300"
                    >
                      Ver Detalles Completos
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Info */}
        <div className="mt-12 text-center">
          <p className="text-white/70 mb-6 text-base md:text-lg">
            ¿No encuentras lo que buscas? Contáctanos directamente
          </p>
          <a
            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, necesito información sobre otros vehículos disponibles')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white/10 text-white px-8 py-4 font-semibold text-lg border border-white/20 hover:bg-white hover:text-black transition-all duration-300"
          >
            <MessageCircle className="w-6 h-6" />
            Hablar con un Asesor
          </a>
        </div>
      </div>
    </section>
  );
};

export default VehiclesList;
